'use client';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  is_read: boolean;
  reference_id: string | null;
  reference_type: string | null;
  created_at: string;
}

type FilterTab = 'all' | 'unread';

// ── Type configuration map ──────────────────────────────────────────────────
const TYPE_CONFIG: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  order_created: {
    label: 'Order Created',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    color: 'text-purple-600 bg-purple-100',
  },
  bid_received: {
    label: 'New Bid',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    color: 'text-blue-600 bg-blue-100',
  },
  bid_accepted: {
    label: 'Bid Accepted',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ),
    color: 'text-green-600 bg-green-100',
  },
  bid_rejected: {
    label: 'Bid Rejected',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    ),
    color: 'text-red-500 bg-red-100',
  },
  delivery_activated: {
    label: 'Delivery Active',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    ),
    color: 'text-orange-500 bg-orange-100',
  },
  new_message: {
    label: 'Message',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
      </svg>
    ),
    color: 'text-indigo-600 bg-indigo-100',
  },
  message: {
    label: 'Message',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
      </svg>
    ),
    color: 'text-indigo-600 bg-indigo-100',
  },
  travel_chat_message: {
    label: 'Travel Chat',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
      </svg>
    ),
    color: 'text-indigo-600 bg-indigo-100',
  },
  storage_chat_message: {
    label: 'Storage Chat',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
      </svg>
    ),
    color: 'text-teal-600 bg-teal-100',
  },
  payout_sent: {
    label: 'Payout',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    color: 'text-emerald-600 bg-emerald-100',
  },
  payout_failed: {
    label: 'Payout Failed',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    color: 'text-red-500 bg-red-100',
  },
  payment_received: {
    label: 'Payment',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    ),
    color: 'text-emerald-600 bg-emerald-100',
  },
  escrow_released: {
    label: 'Escrow',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    color: 'text-emerald-600 bg-emerald-100',
  },
  dispute_opened: {
    label: 'Dispute',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    color: 'text-amber-600 bg-amber-100',
  },
  dispute_resolved: {
    label: 'Dispute Resolved',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    color: 'text-green-600 bg-green-100',
  },
  dispute_message: {
    label: 'Dispute Update',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
      </svg>
    ),
    color: 'text-red-500 bg-red-100',
  },
  trip_approved: {
    label: 'Trip Approved',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ),
    color: 'text-green-600 bg-green-100',
  },
  delivery_update: {
    label: 'Delivery',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    ),
    color: 'text-orange-500 bg-orange-100',
  },
  system: {
    label: 'System',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    color: 'text-gray-600 bg-gray-100',
  },
  verification_update: {
    label: 'Verification',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    color: 'text-blue-600 bg-blue-100',
  },
  delivery_request: {
    label: 'Delivery Request',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    ),
    color: 'text-amber-600 bg-amber-100',
  },
};

function getTypeConfig(type: string) {
  return (
    TYPE_CONFIG[type] ?? {
      label: 'Notification',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
      ),
      color: 'text-muted-foreground bg-muted',
    }
  );
}

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ── Resolve notification click destination ───────────────────────────────────
function getNotificationUrl(n: Notification): string | null {
  const id = n.reference_id;
  const type = n.type;
  const refType = n.reference_type;

  // Message types → delivery messaging
  if (type === 'travel_chat_message' || type === 'new_message' || type === 'message') {
    if (id) return `/delivery-messaging?delivery=${id}`;
    return '/delivery-messaging';
  }
  if (type === 'storage_chat_message') {
    return '/storage-provider';
  }

  // Order-related
  if (type === 'order_created' || type === 'order_expired' || (refType === 'order' && type !== 'bid_accepted' && type !== 'bid_rejected' && type !== 'delivery_activated')) {
    if (id) return `/my-orders`;
    return '/my-orders';
  }

  // Bid-related
  if (type === 'bid_received' || type === 'bid_rejected') {
    if (id) return `/my-bids`;
    return '/my-bids';
  }

  // bid_accepted → go directly to the active delivery
  if (type === 'bid_accepted') {
    if (id && refType === 'delivery') return `/active-deliveries?delivery=${id}`;
    if (id) return `/active-deliveries?delivery=${id}`;
    return '/active-deliveries';
  }

  // Delivery-related
  if (type === 'delivery_activated' || type === 'delivery_update' || refType === 'delivery') {
    if (id) return `/active-deliveries?delivery=${id}`;
    return '/active-deliveries';
  }

  // Payment / escrow
  if (type === 'payment_received' || type === 'escrow_released' || type === 'payout_sent' || type === 'payout_failed') {
    return '/payments-escrow';
  }

  // Dispute
  if (type === 'dispute_opened' || type === 'dispute_resolved' || type === 'dispute_message' || refType === 'dispute') {
    if (id) return `/disputes`;
    return '/disputes';
  }

  // Trip
  if (type === 'trip_approved' || (refType === 'trip' && type !== 'delivery_request')) {
    return '/my-trips';
  }

  // Delivery request (traveler receives this when a customer sends a request)
  if (type === 'delivery_request') {
    return '/active-deliveries';
  }

  // Review
  if (type === 'review_pending' || type === 'review_request') {
    if (id) return `/reviews?tab=pending&delivery=${id}&autoRate=1`;
    return '/reviews?tab=pending';
  }

  // Verification
  if (type === 'verification_update') {
    return '/profile';
  }

  return null;
}

// ── Component ────────────────────────────────────────────────────────────────
export default function NotificationBell() {
  const { user } = useAuth();
  const supabase = createClient();
  const router = useRouter();

  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<FilterTab>('all');
  const [now, setNow] = useState<number>(0);
  const [hasNewRealtime, setHasNewRealtime] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // Track open state in a ref so realtime handler can read it without re-subscribing
  const openRef = useRef(false);
  useEffect(() => { openRef.current = open; }, [open]);

  // Tick every minute to refresh relative timestamps
  useEffect(() => {
    setNow(Date.now());
    const interval = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);
      if (!error && data) setNotifications(data as Notification[]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) fetchNotifications();
  }, [user, fetchNotifications]);

  // Real-time subscription — NOTE: `open` is NOT in deps to avoid re-subscribing on every toggle
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`notifications-bell:${user.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        (payload) => {
          setNotifications((prev) => {
            // Deduplicate by id
            if (prev.some((n) => n.id === (payload.new as Notification).id)) return prev;
            return [payload.new as Notification, ...prev].slice(0, 50);
          });
          // Flash the bell only when dropdown is closed
          if (!openRef.current) setHasNewRealtime(true);
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        (payload) => {
          setNotifications((prev) =>
            prev.map((n) => (n.id === (payload.new as Notification).id ? (payload.new as Notification) : n))
          );
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user]); // Only re-subscribe when user changes

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const visibleNotifications =
    filter === 'unread' ? notifications.filter((n) => !n.is_read) : notifications;

  const markAllRead = async () => {
    if (!user || unreadCount === 0) return;
    const unreadIds = notifications.filter((n) => !n.is_read).map((n) => n.id);
    if (unreadIds.length > 0) {
      await supabase.from('notifications').update({ is_read: true }).in('id', unreadIds);
    }
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
  };

  const toggleRead = async (id: string, currentlyRead: boolean, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const newState = !currentlyRead;
    await supabase.from('notifications').update({ is_read: newState }).eq('id', id);
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: newState } : n)));
  };

  const handleNotificationClick = async (n: Notification) => {
    // Mark as read
    if (!n.is_read) {
      await supabase.from('notifications').update({ is_read: true }).eq('id', n.id);
      setNotifications((prev) => prev.map((item) => item.id === n.id ? { ...item, is_read: true } : item));
    }
    setOpen(false);
    // Navigate to relevant page
    const url = getNotificationUrl(n);
    if (url) router.push(url);
  };

  return (
    <div className="relative" ref={ref}>
      {/* Bell button */}
      <button
        onClick={() => { setOpen((v) => !v); setHasNewRealtime(false); }}
        className={`relative p-2 rounded-lg transition-colors ${
          open
            ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
        }`}
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
      >
        <svg
          className={`w-5 h-5 ${hasNewRealtime ? 'animate-bounce' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {unreadCount > 0 && (
          <span className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none shadow-sm ${hasNewRealtime ? 'animate-pulse' : ''}`}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
        {/* Real-time ping dot when no unread count but new notification arrived */}
        {hasNewRealtime && unreadCount === 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-primary animate-ping" />
        )}
      </button>

      {/* Dropdown — full width on mobile, fixed width on desktop */}
      {open && (
        <div className="absolute right-0 mt-2 w-screen max-w-[360px] sm:w-[360px] bg-card border border-border rounded-2xl shadow-xl z-50 overflow-hidden flex flex-col"
          style={{ maxHeight: 'calc(100vh - 80px)' }}
        >
          {/* ── Header ── */}
          <div className="flex items-center justify-between px-4 pt-4 pb-2 flex-shrink-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-sm text-foreground">Notifications</h3>
              {unreadCount > 0 && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white leading-none">
                  {unreadCount}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-medium transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Mark all read
              </button>
            )}
          </div>

          {/* ── Filter tabs ── */}
          <div className="flex items-center gap-1 px-4 pb-2 flex-shrink-0">
            {(['all', 'unread'] as FilterTab[]).map((tab) => (
              <button
                key={tab}
                onClick={() => setFilter(tab)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors capitalize ${
                  filter === tab
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                {tab}
                {tab === 'unread' && unreadCount > 0 && (
                  <span className="ml-1 opacity-80">({unreadCount})</span>
                )}
              </button>
            ))}
          </div>

          {/* ── Divider ── */}
          <div className="border-t border-border flex-shrink-0" />

          {/* ── Notification list ── */}
          <div className="overflow-y-auto divide-y divide-border/60" style={{ maxHeight: '380px' }}>
            {loading && notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-3">
                <svg className="w-6 h-6 animate-spin text-primary/60" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <p className="text-xs text-muted-foreground">Loading notifications…</p>
              </div>
            ) : visibleNotifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                  <svg className="w-6 h-6 text-muted-foreground/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-foreground">
                  {filter === 'unread' ? 'All caught up!' : 'No notifications yet'}
                </p>
                <p className="text-xs text-muted-foreground text-center px-6">
                  {filter === 'unread' ? 'You have no unread notifications.' : "We'll notify you about bids, messages, payouts, and disputes."}
                </p>
              </div>
            ) : (
              visibleNotifications.map((n) => {
                const cfg = getTypeConfig(n.type);
                const hasLink = !!getNotificationUrl(n);
                return (
                  <div
                    key={n.id}
                    role={hasLink ? 'button' : undefined}
                    tabIndex={hasLink ? 0 : undefined}
                    className={`group relative flex gap-3 px-4 py-3 transition-colors ${
                      hasLink ? 'cursor-pointer hover:bg-muted/60' : 'hover:bg-muted/40'
                    } ${!n.is_read ? 'bg-primary/[0.04]' : ''}`}
                    onClick={() => handleNotificationClick(n)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleNotificationClick(n); }}
                  >
                    {/* Unread indicator bar */}
                    {!n.is_read && (
                      <span className="absolute left-0 top-3 bottom-3 w-0.5 rounded-r-full bg-primary" />
                    )}

                    {/* Type icon */}
                    <div
                      className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${cfg.color}`}
                    >
                      {cfg.icon}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 pr-6">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">
                          {cfg.label}
                        </span>
                        {!n.is_read && (
                          <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                        )}
                        {hasLink && (
                          <svg className="w-3 h-3 text-muted-foreground/50 ml-auto flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        )}
                      </div>
                      <p className="text-xs font-semibold text-foreground leading-snug">{n.title}</p>
                      <p className="text-xs text-muted-foreground leading-snug mt-0.5 break-words whitespace-normal">
                        {n.message}
                      </p>
                      <p className="text-[10px] text-muted-foreground/60 mt-1">
                        {now > 0 ? formatRelativeTime(n.created_at) : ''}
                      </p>
                    </div>

                    {/* Toggle read/unread button (visible on hover) */}
                    <button
                      onClick={(e) => toggleRead(n.id, n.is_read, e)}
                      title={n.is_read ? 'Mark as unread' : 'Mark as read'}
                      className="absolute right-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-full hover:bg-primary/10 text-muted-foreground hover:text-primary"
                    >
                      {n.is_read ? (
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      ) : (
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  </div>
                );
              })
            )}
          </div>

          {/* ── Footer ── */}
          {notifications.length > 0 && (
            <div className="border-t border-border px-4 py-2.5 flex items-center justify-between flex-shrink-0">
              <p className="text-xs text-muted-foreground">
                {visibleNotifications.length} of {notifications.length} notification
                {notifications.length !== 1 ? 's' : ''}
              </p>
              <a
                href="/notifications"
                className="text-xs text-primary hover:text-primary/80 font-medium transition-colors"
                onClick={() => setOpen(false)}
              >
                View all →
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
